import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { cn } from 'lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { isValidColor } from 'helpers/colors';
import Button from 'tailwind-components/Button';

const AUTOPLAY_DELAY_MS = 6000;
const DEFAULT_TEXT_COLOR = '#000';

function resolveAnnouncementTextColor(value) {
  if (value != null && isValidColor(String(value))) {
    return String(value).trim();
  }
  return DEFAULT_TEXT_COLOR;
}

function imageBottomFadeWithColor(color) {
  const c = String(color).trim();
  return {
    background: `linear-gradient(to right, color-mix(in srgb, ${c} 70%, transparent), color-mix(in srgb, ${c} 20%, transparent), transparent)`
  };
}

export default function AnnouncementsCarousel({
  data = [],
  timer = AUTOPLAY_DELAY_MS,
  minHeightClassName = 'min-h-[220px]'
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const item = data[activeIndex];

  const goTo = useCallback(
    index => {
      if (data.length === 0) return;
      setActiveIndex((index + data.length) % data.length);
    },
    [data.length]
  );

  const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
  const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

  useEffect(() => {
    if (data.length <= 1 || isHovered) return;
    const id = setTimeout(goNext, timer);
    return () => clearTimeout(id);
  }, [activeIndex, data.length, timer, goNext, isHovered]);

  if (!data?.length) {
    return (
      <div
        className={cn(
          'min-w-0 max-w-full rounded-xl border border-gray-200 bg-gray-50 p-6 text-center flex items-center justify-center',
          minHeightClassName
        )}
        // the walkthrough probes for this node to tell "announcements hidden by the
        // agency" apart from "rendered but empty", so it has to survive the empty state
        data-tour='announcements'
      >
        <p className='text-sm text-gray-500'>No announcements</p>
      </div>
    );
  }

  const titleResolvedColor = resolveAnnouncementTextColor(item?.titleColor);
  const bodyResolvedColor = resolveAnnouncementTextColor(item?.bodyColor);

  const fadeColorTrimmed =
    item?.fadeColor == null ? '' : String(item.fadeColor).trim();

  const useCustomImageFadeColor =
    item?.showFade && fadeColorTrimmed !== '' && isValidColor(fadeColorTrimmed);

  const fadeOverlayClassName =
    !useCustomImageFadeColor && item?.showFade
      ? 'bg-linear-to-r from-gray-700/95 via-gray-700/60 to-transparent'
      : '';

  const content = (
    <div
      className='relative w-full min-w-0 max-w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-200 group'
      data-tour='announcements'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background image + overlay (absolute; height follows content below) */}
      {item?.image && (
        <img
          src={item.image}
          alt=''
          className='pointer-events-none absolute inset-0 z-0 size-full object-cover transition-opacity duration-500'
          style={{ objectPosition: item.objectPosition || 'center' }}
        />
      )}

      <div
        className={cn(
          'pointer-events-none absolute inset-0 z-10',
          fadeOverlayClassName
        )}
        style={
          useCustomImageFadeColor
            ? imageBottomFadeWithColor(fadeColorTrimmed)
            : undefined
        }
      />

      {/* Foreground: in-flow so card grows with title/body on narrow screens */}
      <div
        className={cn(
          'relative z-20 flex min-w-0 w-full max-w-full flex-col justify-between gap-4 p-4 pt-5 sm:pt-4',
          minHeightClassName
        )}
      >
        {/* Top: label + title + body */}
        <div
          className={cn(
            'min-w-0 max-w-full',
            item?.showFade ? 'text-gray-900' : 'text-white'
          )}
        >
          <span
            className={cn(
              'mb-1 block text-[10px] font-semibold uppercase tracking-widest',
              item?.showFade ? 'text-grey-light' : 'text-white/90'
            )}
          >
            Announcements
          </span>
          <span
            className='block min-w-0 max-w-full text-sm font-semibold leading-snug wrap-anywhere'
            style={{ color: titleResolvedColor }}
          >
            {item?.title}
          </span>
          <p
            className='mt-1 min-w-0 max-w-full text-xs leading-relaxed wrap-anywhere'
            style={{ color: bodyResolvedColor }}
          >
            {item?.body}
          </p>
          {item?.url && item?.buttonText && (
            <a
              href={item.url}
              target='_blank'
              rel='noreferrer noopener'
              className='mt-2 block min-w-0 max-w-full w-full'
            >
              <Button
                text={item.buttonText}
                variant={item?.buttonVariant || 'primaryDarkened'}
                size={item?.buttonSize || 'medium'}
                rounded={item?.buttonRoundness || 'medium'}
                fill
                className='h-auto! max-w-full min-w-0 justify-center text-center [&>span:last-child]:min-w-0 [&>span:last-child]:max-w-full [&>span:last-child]:whitespace-normal [&>span:last-child]:wrap-break-word'
              />
            </a>
          )}
        </div>

        {/* Bottom: prev arrow — dots — next arrow */}
        {data.length > 1 && (
          <div className='flex min-w-0 shrink-0 items-center justify-between gap-2'>
            {/* Prev */}
            <button
              type='button'
              aria-label='Previous slide'
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                goPrev();
              }}
              className='flex cursor-pointer items-center justify-center w-6 h-6 rounded-full bg-black/30 hover:bg-black/50 text-white backdrop-blur-sm border border-white/20 transition-colors duration-200 shrink-0'
            >
              <ChevronLeft className='w-3.5 h-3.5' />
            </button>

            {/* Dots */}
            <div className='flex min-w-0 flex-1 justify-center items-center gap-1.5'>
              {data.map((_, i) => (
                <button
                  type='button'
                  key={`dot-${i}`}
                  aria-label={`Go to slide ${i + 1}`}
                  aria-current={i === activeIndex}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveIndex(i);
                  }}
                  // there's no preflight, so border-0 and p-0 drop the native button bevel
                  className={cn(
                    'size-2 shrink-0 cursor-pointer rounded-full border-0 p-0 transition-colors duration-300',
                    i === activeIndex
                      ? 'bg-white'
                      : 'bg-white/45 hover:bg-white/75'
                  )}
                />
              ))}
            </div>

            {/* Next */}
            <button
              type='button'
              aria-label='Next slide'
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                goNext();
              }}
              className='flex cursor-pointer items-center justify-center w-6 h-6 rounded-full bg-black/30 hover:bg-black/50 text-white backdrop-blur-sm border border-white/20 transition-colors duration-200 shrink-0'
            >
              <ChevronRight className='w-3.5 h-3.5' />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (item?.url && !item?.buttonText) {
    return (
      <a
        href={item.url}
        target='_blank'
        rel='noreferrer noopener'
        className='block w-full min-w-0 max-w-full rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
      >
        {content}
      </a>
    );
  }

  return <div className='w-full min-w-0 max-w-full'>{content}</div>;
}

AnnouncementsCarousel.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      body: PropTypes.string,
      image: PropTypes.string,
      url: PropTypes.string,
      objectFit: PropTypes.string,
      objectPosition: PropTypes.string,
      showFade: PropTypes.bool,
      titleColor: PropTypes.string,
      bodyColor: PropTypes.string,
      fadeColor: PropTypes.string,
      buttonText: PropTypes.string,
      buttonVariant: PropTypes.string,
      buttonSize: PropTypes.string,
      buttonRoundness: PropTypes.string
    })
  ),
  timer: PropTypes.number,
  /** height floor of the slide, so tighter layouts can shrink the banner */
  minHeightClassName: PropTypes.string
};
